import { useCallback, useEffect, useRef, useState } from 'react';
import {
  programmeById, programmeStaff, normProgramme, normItem,
  loadLibraryData, rowsOf, patchProgramme, patchProgrammeItem,
  createProgrammeItem, deleteProgrammeItem, uploadProgrammeCover,
  programmeBody, programmeItemBody,
} from './programmeApi';

const responseAlerts = response => {
  const row = response?.programme ?? response;
  const alerts = response?.rightsAlerts
    ?? response?.rights_alerts
    ?? response?.alerts
    ?? row?.rightsAlerts
    ?? row?.rights_alerts
    ?? row?.alerts;
  return rowsOf(alerts);
};

export default function useProgramme(id) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [programme, setProgramme] = useState(null);
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [library, setLibrary] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [saveState, setSaveState] = useState('saved');
  const timers = useRef({});
  const patches = useRef({});

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    const load = async () => {
      try {
        const response = await programmeById(id);
        const row = response?.programme ?? response;
        if (!row) throw new Error('Programme not found.');

        const [staffResult, libraryResult] = await Promise.allSettled([
          programmeStaff(id),
          loadLibraryData(),
        ]);
        if (!active) return;

        setProgramme(normProgramme(row));
        setItems(rowsOf(response?.items ?? row.items).map(normItem).sort((a, b) => a.order - b.order));
        setAlerts(responseAlerts(response));
        setStaff(staffResult.status === 'fulfilled' ? rowsOf(staffResult.value) : []);
        setLibrary(libraryResult.status === 'fulfilled' ? libraryResult.value.library : []);
        if (libraryResult.status === 'rejected') throw libraryResult.reason;
      } catch (err) {
        if (active) setError(err?.message || 'Could not load programme.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [id]);

  const flush = useCallback(async key => {
    const pending = patches.current[key];
    delete patches.current[key];
    delete timers.current[key];
    if (!pending) return;
    try {
      const result = pending.kind === 'programme'
        ? await patchProgramme(id, programmeBody(pending.body))
        : await patchProgrammeItem(id, pending.itemId, programmeItemBody(pending.body));
      if (result?.ok === false) throw new Error('The programme API rejected the update.');
      if (Object.keys(timers.current).length === 0) setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  }, [id]);

  const queue = useCallback((key, kind, itemId, body, delay = 700) => {
    patches.current[key] = {
      ...(patches.current[key] || {}),
      kind,
      itemId,
      body: { ...(patches.current[key]?.body || {}), ...body },
    };
    setSaveState('saving');
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => flush(key), delay);
  }, [flush]);

  const updateProgramme = patch => {
    setProgramme(current => current ? { ...current, ...patch } : current);
    queue('p', 'programme', null, patch);
  };

  const updateItem = (itemId, patch) => {
    setItems(current => current.map(item => (item.id === itemId ? { ...item, ...patch } : item)));
    queue(`i${itemId}`, 'item', itemId, patch);
  };

  const addItem = async (type, piece) => {
    const base = {
      type,
      title: piece?.title || (type === 'hymn' ? 'Hymn' : type.charAt(0).toUpperCase() + type.slice(1)),
      ...(type === 'hymn' && piece?.id ? { pieceId: piece.id } : {}),
      instrument: 'Piano',
      sortOrder: items.length,
      versesChosen: [],
      hostOwnText: false,
      hostTextConfirmed: false,
      text: '',
      notes: '',
    };
    setSaveState('saving');
    try {
      const result = await createProgrammeItem(id, programmeItemBody(base));
      if (!result?.id) throw new Error('The programme API did not return an item id.');
      setItems(current => [...current, normItem({ ...base, id: result.id })]);
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  };

  const removeItem = async itemId => {
    setSaveState('saving');
    try {
      await deleteProgrammeItem(id, itemId);
      setItems(current => current.filter(item => item.id !== itemId).map((item, index) => ({ ...item, order: index })));
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  };

  const moveItem = (from, to) => {
    if (to < 0 || to >= items.length || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const reordered = next.map((item, index) => ({ ...item, order: index }));
    setItems(reordered);
    reordered.forEach(item => {
      if (items.findIndex(existing => existing.id === item.id) !== item.order) {
        queue(`i${item.id}`, 'item', item.id, { sortOrder: item.order }, 300);
      }
    });
  };

  const uploadCover = async file => {
    setSaveState('saving');
    try {
      await uploadProgrammeCover(id, file);
      const response = await programmeById(id);
      const row = response?.programme ?? response;
      setProgramme(normProgramme(row));
      setSaveState('saved');
      return true;
    } catch {
      setSaveState('error');
      return false;
    }
  };

  useEffect(() => () => {
    Object.keys(timers.current).forEach(key => clearTimeout(timers.current[key]));
  }, []);

  return {
    loading, error, programme, items, staff, library, alerts, saveState,
    updateProgramme, updateItem, addItem, removeItem, moveItem, uploadCover,
  };
}